import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Edit, Lock } from "react-feather";
import DataTable from "../../../components/common/DataTable";
import { confirmDelete } from "../../../components/common/ConfirmDialog";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useCanViewServiceTypes, useCanManageServiceTypes } from "./useServiceTypePermissions";
import {
  useGetServiceTypesQuery,
  useCreateServiceTypeMutation,
  useUpdateServiceTypeMutation,
  useDeleteServiceTypeMutation,
} from "./serviceTypesApi";

const KIND_OPTIONS = [
  { value: "GUARDS", label: "Guards" },
  { value: "ALARMS", label: "Alarms" },
  { value: "OTHER", label: "Other" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "", label: "All Statuses" },
];

const KIND_BADGE = { GUARDS: "badge-linesuccess", ALARMS: "badge-lineinfo", OTHER: "badge-linewarning" };

// Branch-wise master (handoff §9.9, added 2026-08-19) - the "which service
// is this enquiry for" checklist admins configure per branch, feeding
// Flutter's Sales Enquiry picker (§17.10). NOT the pre-existing, global,
// still-Flutter-owned Guard Service Type master (§17.9.1) - same generic
// name, unrelated concept, only used inside one Guard Requirement line.
// GUARDS/ALARMS rows are protected: server refuses delete on them (rename/
// activate-deactivate still work), so Delete is disabled for those kinds
// here rather than round-tripping to hit the server refusal.
const ServiceTypesPage = () => {
  const canView = useCanViewServiceTypes();
  const canManage = useCanManageServiceTypes();
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const [status, setStatus] = useState("active");
  const { data: records = [], isLoading } = useGetServiceTypesQuery(
    {
      branchId: selectedBranchId ?? undefined,
      isActive: status === "" ? undefined : status === "active",
    },
    { skip: !canView }
  );
  const [createServiceType, { isLoading: isCreating }] = useCreateServiceTypeMutation();
  const [updateServiceType, { isLoading: isUpdating }] = useUpdateServiceTypeMutation();
  const [deleteServiceType] = useDeleteServiceTypeMutation();

  const [mode, setMode] = useState("new"); // "new" | "view" | "edit"
  const [selectedId, setSelectedId] = useState(null);
  const [branchId, setBranchId] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState("OTHER");
  const [isActive, setIsActive] = useState(true);
  const [search, setSearch] = useState("");

  const selectedRecord = records.find((r) => r.serviceTypeId === selectedId) || null;
  const isProtected = selectedRecord?.serviceKind === "GUARDS" || selectedRecord?.serviceKind === "ALARMS";

  const filteredRecords = search.trim()
    ? records.filter((r) => r.serviceTypeName.toLowerCase().includes(search.trim().toLowerCase()))
    : records;

  useEffect(() => {
    if (mode === "new") {
      setName("");
      setKind("OTHER");
      setIsActive(true);
      setBranchId(
        selectedBranchId ? String(selectedBranchId) : availableBranches[0]?.branchId ? String(availableBranches[0].branchId) : ""
      );
    } else if (selectedRecord) {
      setBranchId(String(selectedRecord.branchId));
      setName(selectedRecord.serviceTypeName);
      setKind(selectedRecord.serviceKind);
      setIsActive(selectedRecord.isActive);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedRecord]);

  const isReadOnly = mode === "view";
  const canWrite = canManage;

  const select = (id) => {
    setSelectedId(id);
    setMode("view");
  };
  const startEdit = () => setMode("edit");
  const startNew = () => {
    setSelectedId(null);
    setMode("new");
  };

  const handleSave = async () => {
    if (!branchId) {
      toast.error("Branch is required");
      return;
    }
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      if (mode === "edit" && selectedRecord) {
        await updateServiceType({
          serviceTypeId: selectedRecord.serviceTypeId,
          serviceTypeName: name.trim(),
          isActive,
        }).unwrap();
      } else {
        await createServiceType({
          branchId: Number(branchId),
          serviceTypeName: name.trim(),
          serviceKind: kind,
        }).unwrap();
      }
      toast.success("Service type saved");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not save service type");
    }
  };

  const handleDelete = async () => {
    if (!selectedRecord || isProtected) return;
    const confirmed = await confirmDelete({ title: `Delete ${selectedRecord.serviceTypeName}?` });
    if (!confirmed) return;
    try {
      await deleteServiceType(selectedRecord.serviceTypeId).unwrap();
      toast.success("Service type deleted");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not delete service type");
    }
  };

  const columns = [
    { title: "Name", dataIndex: "serviceTypeName" },
    {
      title: "Kind",
      dataIndex: "serviceKind",
      render: (value) => (
        <span className={`badge ${KIND_BADGE[value] || "badge-linewarning"} d-inline-flex align-items-center gap-1`}>
          {(value === "GUARDS" || value === "ALARMS") && <Lock size={11} />}
          {KIND_OPTIONS.find((k) => k.value === value)?.label || value}
        </span>
      ),
    },
    {
      title: "Branch",
      dataIndex: "branchId",
      render: (value) => availableBranches.find((b) => b.branchId === value)?.branchCode || value,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (value) => (
        <span className={`badge ${value ? "badge-linesuccess" : "badge-linedanger"}`}>
          {value ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      width: 60,
      render: (_, record) => (
        <button type="button" className="btn btn-link p-1" onClick={() => select(record.serviceTypeId)}>
          <Edit size={14} />
        </button>
      ),
    },
  ];

  if (!canView) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-info">You do not have permission to view Service Types.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Service Types</h4>
              <h6>Per-branch catalog of services Sales enquiries can request</h6>
            </div>
          </div>
        </div>

        {!canManage && (
          <div className="alert alert-info">
            You have read-only access. Managing service types requires Sales.ManageServiceTypes.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Details</h5>
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Branch</label>
                <select
                  className="form-control"
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  disabled={isReadOnly || mode === "edit" || !canWrite}
                >
                  <option value="">--Select--</option>
                  {availableBranches.map((b) => (
                    <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  readOnly={isReadOnly || !canWrite}
                  disabled={isReadOnly || !canWrite}
                />
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label">Kind</label>
                {mode === "new" ? (
                  <select
                    className="form-control"
                    value={kind}
                    onChange={(e) => setKind(e.target.value)}
                    disabled={!canWrite}
                  >
                    {KIND_OPTIONS.map((k) => (
                      <option key={k.value} value={k.value}>{k.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="form-control"
                    value={KIND_OPTIONS.find((k) => k.value === kind)?.label || kind}
                    readOnly
                    disabled
                    title="Kind is set at creation and can't be changed afterward"
                  />
                )}
              </div>
              {mode !== "new" && (
                <div className="col-md-2 mb-3">
                  <label className="form-label d-block">Active</label>
                  <div className="form-check form-switch mt-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      disabled={isReadOnly || !canWrite}
                    />
                  </div>
                </div>
              )}
            </div>

            {isProtected && mode !== "new" && (
              <div className="alert alert-warning py-2 px-3 d-inline-flex align-items-center gap-2 mb-3">
                <Lock size={14} />
                Guards/Alarms service types are protected and cannot be deleted. Rename and active/inactive toggling still work.
              </div>
            )}

            {canManage && (
              <div className="d-flex gap-2">
                {mode !== "view" && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={isCreating || isUpdating}
                  >
                    Save
                  </button>
                )}
                {mode === "view" && (
                  <button type="button" className="btn btn-outline-primary" onClick={startEdit}>
                    Edit
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={startNew}>
                  Clear
                </button>
                {mode === "view" && (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={handleDelete}
                    disabled={isProtected}
                    title={isProtected ? "Guards/Alarms service types cannot be deleted" : undefined}
                  >
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search service type name"
                    className="form-control form-control-sm formsearch"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={filteredRecords}
                rowKey="serviceTypeId"
                loading={isLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceTypesPage;
